/**
 * Booking cutoff helpers — mirror the backend logic in server.py::validate_booking_cutoff.
 * All time comparisons are done in Europe/Rome wall-clock time to match the backend, regardless
 * of the user's browser timezone.
 */

/** Parse "HH:MM" into {h, m}. */
const parseHM = (s) => {
  const [h, m] = (s || "00:00").split(":").map(Number);
  return { h: Number.isFinite(h) ? h : 0, m: Number.isFinite(m) ? m : 0 };
};

/** Returns a new Date at (year, month, day, hour, minute, 0, 0) using the local clock of `base`. */
const atHM = (date, h, m) => {
  const d = new Date(date);
  d.setHours(h, m, 0, 0);
  return d;
};

/** Returns a Date object whose *local* clock matches current Europe/Rome wall-clock time.
 *  This lets all downstream comparisons use plain local-Date math even if the user's browser
 *  is in another timezone (Playwright in UTC, tourist in PST, etc.). */
const nowInRome = () => {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/Rome",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hour12: false,
    }).formatToParts(new Date());
    const g = (t) => parts.find((p) => p.type === t)?.value;
    return new Date(
      Number(g("year")),
      Number(g("month")) - 1,
      Number(g("day")),
      Number(g("hour")),
      Number(g("minute")),
      Number(g("second") || 0),
      0
    );
  } catch {
    // Extremely old browsers — fall back to browser local
    return new Date();
  }
};

/** Returns a Date for the earliest allowed pickup datetime based on the booking settings,
 *  given the provided "now" (defaults to current Europe/Rome wall-clock, so cutoff logic
 *  matches the backend regardless of the user's browser timezone). */
export function earliestPickupDateTime(settings, now = nowInRome()) {
  if (!settings) return now;
  const { h: ws, m: wsm } = parseHM(settings.cutoff_window_start);
  const { h: we, m: wem } = parseHM(settings.cutoff_window_end);
  const { h: dh, m: dm } = parseHM(settings.default_next_day_pickup_time);
  const minHours = Number(settings.min_cutoff_hours || 0);

  const winStart = atHM(now, ws, wsm);
  const winEnd = atHM(now, we, wem);

  // Support overnight windows where end < start (e.g. 22:00 -> 02:00)
  const insideWindow = winStart <= winEnd
    ? now >= winStart && now <= winEnd
    : now >= winStart || now <= winEnd;

  if (insideWindow) {
    return new Date(now.getTime() + minHours * 3600 * 1000);
  }
  // Outside window: next occurrence of the default pickup time (today if still in the future, else tomorrow)
  const todayDefault = atHM(now, dh, dm);
  if (todayDefault > now) return todayDefault;
  const tomorrowDefault = new Date(todayDefault);
  tomorrowDefault.setDate(tomorrowDefault.getDate() + 1);
  return tomorrowDefault;
}

/** Strip time from a Date (returns local midnight). */
const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

/** Given booking settings, is the given calendar date selectable (at least one valid slot remains)?
 *  The Date passed by react-day-picker represents a whole day (midnight). */
export function isDateSelectable(settings, date, now = nowInRome()) {
  const earliest = earliestPickupDateTime(settings, now);
  // The date is selectable if its end-of-day >= earliest pickup datetime
  const dayEnd = atHM(date, 23, 59);
  return dayEnd >= earliest && startOfDay(date) >= startOfDay(earliest) - 0;
}

/** Return a time string "HH:MM" that is the minimum allowed pickup for the given date.
 *  If the date is strictly in the future, returns "00:00". If it equals the earliest
 *  pickup date, returns that earliest time formatted. */
export function minTimeForDate(settings, date, now = nowInRome()) {
  const earliest = earliestPickupDateTime(settings, now);
  if (startOfDay(date).getTime() > startOfDay(earliest).getTime()) return "00:00";
  if (startOfDay(date).getTime() < startOfDay(earliest).getTime()) return "23:59"; // effectively unbookable
  // Same day: earliest time
  const h = earliest.getHours().toString().padStart(2, "0");
  const m = earliest.getMinutes().toString().padStart(2, "0");
  return `${h}:${m}`;
}

/** Given a list of "HH:MM" slots, return only those >= the minimum for this date. */
export function filterSlotsForDate(slots, settings, date, now = nowInRome()) {
  if (!date) return slots;
  const min = minTimeForDate(settings, date, now);
  // Round up to the nearest 30-min slot if min is not on a slot boundary
  return slots.filter((s) => s >= min);
}

/** Format a Date as "HH:MM" using its local clock (which we've aligned to Europe/Rome). */
const fmtHM = (d) =>
  `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;

/** Format a Date as "DD MMM" (English) using its local clock. */
const fmtDayMonth = (d) => {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${d.getDate()} ${months[d.getMonth()]}`;
};

/** Friendly helper text like "Earliest pickup today: 13:12" or
 *  "Earliest pickup: 30 Apr at 10:00" when the earliest falls on a future day. */
export function earliestPickupLabel(settings, now = nowInRome()) {
  if (!settings) return "";
  const earliest = earliestPickupDateTime(settings, now);
  const sameDay = earliest.getFullYear() === now.getFullYear()
    && earliest.getMonth() === now.getMonth()
    && earliest.getDate() === now.getDate();
  const time = fmtHM(earliest);
  return sameDay
    ? `Earliest pickup today: ${time}`
    : `Earliest pickup: ${fmtDayMonth(earliest)} at ${time}`;
}

